import { IconDeviceMobile, IconLock, IconMail } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import axios from "axios";
import { useState } from "react";
import { z } from "zod";
import { Button, Checkbox, Group, Input, OtpInput, Stack, Text } from "@/components/ui";
import { useAuthStore } from "@/lib/auth-store";
import { toastError, toastExito } from "@/lib/toast";
import { esRetoDosFactores, login, login2FA } from "@/shared/api/auth.api";
import type { ApiProblem, AuthResponse, LoginRequest } from "@/lib/types";

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "La contraseña es obligatoria"),
});

type LoginValues = z.infer<typeof loginSchema>;
type LoginErrors = Partial<Record<keyof LoginValues, string>>;

/** "Recordar usuario" · solo se persiste el EMAIL (jamás la contraseña). */
const REMEMBER_KEY = "multiva.remember-email";

function leerEmailRecordado(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) ?? "";
  } catch {
    return "";
  }
}

function guardarEmailRecordado(email: string, recordar: boolean): void {
  try {
    if (recordar) localStorage.setItem(REMEMBER_KEY, email);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // storage bloqueado (modo privado estricto) · la sesión funciona igual
  }
}

/** Reto 2FA pendiente (P36) · el password ya pasó, falta el código. */
interface RetoPendiente {
  email: string;
  twoFactorToken: string;
}

export function LoginForm() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [reto, setReto] = useState<RetoPendiente | null>(null);

  const emailRecordado = leerEmailRecordado();
  const [recordar, setRecordar] = useState(emailRecordado !== "");
  const [values, setValues] = useState<LoginValues>({ email: emailRecordado, password: "" });
  const [errors, setErrors] = useState<LoginErrors>({});

  function entrar(data: AuthResponse) {
    setSession(data);
    toastExito({ title: "Bienvenido", message: data.user.fullName || data.user.email });
    void navigate({ to: "/dashboard" as never, replace: true });
  }

  const mutation = useMutation({
    mutationFn: (req: LoginRequest) => login(req),
    onSuccess: (data, req) => {
      // "Recordar usuario" · el password fue correcto (o llegó al reto 2FA).
      guardarEmailRecordado(req.email, recordar);
      // 2FA (P36) · el password pasó pero falta el segundo factor.
      if (esRetoDosFactores(data)) {
        setReto({ email: req.email, twoFactorToken: data.twoFactorToken });
        return;
      }
      entrar(data);
    },
    onError: (err: unknown) => {
      toastError({ title: "No se pudo iniciar sesión", message: extractMessage(err) });
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = loginSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: LoginErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof LoginValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data);
  }

  if (reto) {
    return <TwoFactorStep reto={reto} onVerificado={entrar} onVolver={() => setReto(null)} />;
  }

  return (
    // `noValidate`: los campos NO llevan `required` nativo · la validación
    // corre por zod al enviar (antes competían: con un campo vacío ganaba
    // la burbuja nativa del navegador en inglés y el mensaje de zod en
    // español nunca se veía · hallazgo de COMPOSICION-PANTALLAS.md §2.0).
    <form onSubmit={onSubmit} noValidate>
      <Stack gap="sm">
        <Input
          label="Correo electrónico"
          placeholder="tu.correo@proveedores.test"
          autoComplete="email"
          leftSection={<IconMail size={16} stroke={1.6} />}
          size="md"
          id="email"
          value={values.email}
          onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
          error={errors.email}
        />
        <Input
          type="password"
          label="Contraseña"
          placeholder="•••••••"
          autoComplete="current-password"
          leftSection={<IconLock size={16} stroke={1.6} />}
          size="md"
          id="password"
          value={values.password}
          onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
          error={errors.password}
        />
        <Group justify="space-between" className="mt-0.5">
          <Checkbox
            label="Recordar usuario"
            checked={recordar}
            onCheckedChange={(checked) => {
              const marcado = checked === true;
              setRecordar(marcado);
              // Desmarcar borra el recuerdo al instante (no espera al login).
              if (!marcado) guardarEmailRecordado("", false);
            }}
          />
          <Link to="/recuperar-contrasena" className="text-sm text-multiva-600 hover:underline">
            ¿Olvidaste tu contraseña?
          </Link>
        </Group>
        <Button type="submit" fullWidth loading={mutation.isPending} size="md" className="mt-1">
          {mutation.isPending ? "Verificando…" : "Ingresar al portal"}
        </Button>
      </Stack>
    </form>
  );
}

/**
 * Segundo paso del login (P36) · código de 6 dígitos del autenticador o,
 * como alternativa, un código de recuperación de un solo uso.
 */
function TwoFactorStep({
  reto,
  onVerificado,
  onVolver,
}: {
  reto: RetoPendiente;
  onVerificado: (data: AuthResponse) => void;
  onVolver: () => void;
}) {
  const [codigo, setCodigo] = useState("");
  const [modoRecuperacion, setModoRecuperacion] = useState(false);
  const [codigoRecuperacion, setCodigoRecuperacion] = useState("");

  const verificar = useMutation({
    mutationFn: () =>
      login2FA({
        email: reto.email,
        twoFactorToken: reto.twoFactorToken,
        codigo: modoRecuperacion ? codigoRecuperacion : codigo,
        esCodigoRecuperacion: modoRecuperacion,
      }),
    onSuccess: onVerificado,
    onError: (err: unknown) => {
      setCodigo("");
      toastError({ title: "Código no válido", message: extractMessage(err) });
    },
  });

  const puedeVerificar = modoRecuperacion ? codigoRecuperacion.trim().length > 0 : codigo.length === 6;

  return (
    <Stack gap="md">
      <Stack gap={4} align="center">
        <IconDeviceMobile size={28} stroke={1.5} className="text-multiva-600" />
        <Text fw={600}>Verificación en dos pasos</Text>
        <Text size="sm" c="dimmed" ta="center">
          {modoRecuperacion
            ? "Ingresa uno de tus códigos de recuperación de un solo uso."
            : "Ingresa el código de 6 dígitos de tu app de autenticación (Google Authenticator)."}
        </Text>
      </Stack>

      {modoRecuperacion ? (
        <Input
          placeholder="XXXXX-XXXXX"
          size="md"
          value={codigoRecuperacion}
          onChange={(e) => setCodigoRecuperacion(e.target.value)}
          autoFocus
          aria-label="Código de recuperación"
          className="text-center font-mono"
        />
      ) : (
        <div className="flex justify-center">
          <OtpInput length={6} value={codigo} onChange={setCodigo} onComplete={() => verificar.mutate()} />
        </div>
      )}

      <Button fullWidth size="md" loading={verificar.isPending} disabled={!puedeVerificar} onClick={() => verificar.mutate()}>
        Verificar
      </Button>

      <Stack gap={4} align="center">
        <button
          type="button"
          className="text-xs text-multiva-600 hover:underline"
          onClick={() => {
            setModoRecuperacion((m) => !m);
            setCodigo("");
            setCodigoRecuperacion("");
          }}
        >
          {modoRecuperacion ? "Usar el código del autenticador" : "¿Perdiste el dispositivo? Usa un código de recuperación"}
        </button>
        <button type="button" className="text-xs text-multiva-600 hover:underline" onClick={onVolver}>
          Volver al inicio de sesión
        </button>
      </Stack>
    </Stack>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError<ApiProblem>(err)) {
    const data = err.response?.data;
    if (data?.detail) return data.detail;
    if (data?.title) return data.title;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return "No se pudo iniciar sesión";
}
